/**
 * Примитивы интерфейса.
 *
 * Размеры продиктованы полем, а не вкусом: перчатки, солнце в экран, тряска.
 * Минимальная цель нажатия — 56px у главных действий (при рекомендованных 44
 * это запас на промах), крупный шрифт, высокий контраст.
 */
import type { ButtonHTMLAttributes, ReactNode } from 'react'

type Tone = 'primary' | 'danger' | 'neutral'

const toneClass: Record<Tone, string> = {
  primary: 'bg-emerald-500 text-black active:bg-emerald-600',
  danger: 'bg-red-500 text-white active:bg-red-600',
  neutral: 'border border-white/20 text-white active:bg-white/10',
}

export function BigButton({
  tone = 'neutral',
  busy,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { tone?: Tone; busy?: boolean }) {
  return (
    <button
      {...rest}
      disabled={rest.disabled || busy}
      className={`min-h-[56px] flex-1 rounded-xl px-4 text-base font-medium transition
        disabled:opacity-40 ${toneClass[tone]} ${rest.className ?? ''}`}
    >
      {busy ? '…' : children}
    </button>
  )
}

/** Строка «показатель — значение». Значение крупнее подписи: его и читают. */
export function Metric({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="rounded-lg bg-white/5 px-3 py-2">
      <div className="text-xs uppercase tracking-wide opacity-50">{label}</div>
      <div className="mt-0.5 text-lg">{value}</div>
    </div>
  )
}

type NoticeTone = 'info' | 'warn' | 'error' | 'ok'

const noticeClass: Record<NoticeTone, string> = {
  info: 'bg-white/10 text-white/80',
  warn: 'bg-amber-500/15 text-amber-200',
  error: 'bg-red-500/15 text-red-300',
  ok: 'bg-emerald-500/15 text-emerald-200',
}

export function Notice({ tone = 'info', children }: { tone?: NoticeTone; children: ReactNode }) {
  return (
    <p
      role={tone === 'error' ? 'alert' : undefined}
      className={`rounded-lg px-4 py-3 text-sm ${noticeClass[tone]}`}
    >
      {children}
    </p>
  )
}

/** Тон причины — словарь из describeBike, а не общий NoticeTone: семь красок. */
export type ChipTone = 'ok' | 'blue' | 'red' | 'orange' | 'purple' | 'grey' | 'neutral'

const chipClass: Record<ChipTone, string> = {
  ok: 'bg-emerald-500/15 text-emerald-200',
  blue: 'bg-sky-500/15 text-sky-200',
  red: 'bg-red-500/15 text-red-300',
  orange: 'bg-amber-500/15 text-amber-200',
  purple: 'bg-purple-500/15 text-purple-200',
  grey: 'bg-white/10 text-white/70',
  neutral: 'bg-white/10 text-white/70',
}

/** Короткий цветной ярлык причины — списки и попапы на карте. */
export function Chip({ tone = 'neutral', children }: { tone?: ChipTone; children: ReactNode }) {
  return (
    <span className={`inline-block rounded-full px-3 py-1 text-xs font-medium ${chipClass[tone]}`}>
      {children}
    </span>
  )
}

/**
 * Нижний лист — форма подтверждения/выбора, не занимающая отдельный экран.
 *
 * §2.2 спецификации прямо запрещает вложенные экраны для второстепенных
 * действий («замечание диспетчеру — одна кнопка внизу, открывает нижний
 * лист с текстом»): переход на новый маршрут потерял бы контекст карточки
 * (заряд, замок, номер), к которому лист обычно и апеллирует.
 */
export function BottomSheet({
  open,
  onClose,
  children,
}: {
  open: boolean
  onClose: () => void
  children: ReactNode
}) {
  if (!open) return null
  return (
    <div className="fixed inset-0 z-30 flex items-end" role="dialog" aria-modal="true">
      <button
        aria-label="Закрыть"
        className="absolute inset-0 bg-black/60"
        onClick={onClose}
      />
      <div
        className="relative max-h-[80dvh] w-full overflow-y-auto rounded-t-2xl border-t
          border-white/10 bg-[#16181d] p-4"
        style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
      >
        {children}
      </div>
    </div>
  )
}
